import { distanceMetres, type Fix } from "./geo";

export type FenceMode = "block" | "flag";
export type LocationUnavailableMode = "approval" | "flag";
export type LocationStatus = "inside" | "outside" | "unavailable";
export type QrStatus = "valid" | "invalid" | "none";

export type PunchFlag =
  | "outside_fence"
  | "location_unavailable"
  | "site_mismatch"
  | "no_qr"
  | "offline_sync"
  | "not_assigned"
  /** Clocked out after the visit had already been marked "missing clock-out". */
  | "late_clock_out";

/** Fixes with worse accuracy than this are treated as "location unavailable/poor". */
export const MAX_USABLE_ACCURACY_M = 150;
/** At most this much of the reported accuracy is added to the radius when deciding "inside". */
export const ACCURACY_ALLOWANCE_CAP_M = 50;

export interface FenceSite {
  lat: number;
  lng: number;
  radiusM: number;
}

export interface LocationResult {
  status: LocationStatus;
  /** Distance from the site pin, when a fix was available. */
  distanceM: number | null;
}

/**
 * Classifies a fix against a site's geofence (PLAN.md §3 "Geofence maths"):
 * - inside if distance ≤ radius + min(accuracy, 50 m)
 * - clearly outside if distance − accuracy > radius
 * - anything else, no fix, or accuracy worse than 150 m → unavailable/poor
 */
export function classifyLocation(site: FenceSite, fix: Fix | null): LocationResult {
  if (!fix) return { status: "unavailable", distanceM: null };
  const distanceM = distanceMetres(site.lat, site.lng, fix.lat, fix.lng);
  if (!(fix.accuracy <= MAX_USABLE_ACCURACY_M)) return { status: "unavailable", distanceM };
  if (distanceM <= site.radiusM + Math.min(fix.accuracy, ACCURACY_ALLOWANCE_CAP_M)) {
    return { status: "inside", distanceM };
  }
  if (distanceM - fix.accuracy > site.radiusM) return { status: "outside", distanceM };
  return { status: "unavailable", distanceM };
}

export interface PolicyInput {
  qr: QrStatus;
  location: LocationStatus;
  /** True when the fix is clearly outside this site but inside another site's fence. */
  insideOtherSite: boolean;
  fenceMode: FenceMode;
  locationUnavailableMode: LocationUnavailableMode;
}

export type PolicyDecision =
  | { outcome: "accept"; flags: PunchFlag[] }
  | { outcome: "needs_approval"; reason: PunchFlag; flags: PunchFlag[] }
  | { outcome: "reject"; reason: "invalid_qr" };

function locationFlag(location: LocationStatus, insideOtherSite: boolean): PunchFlag[] {
  if (location === "inside") return [];
  if (location === "unavailable") return ["location_unavailable"];
  return insideOtherSite ? ["site_mismatch", "outside_fence"] : ["outside_fence"];
}

/**
 * The Block/Flag table from PLAN.md §3. Pure function: no I/O.
 *
 * | Situation                         | Block mode           | Flag mode                |
 * | Invalid / old QR                  | reject               | reject                   |
 * | Valid QR, inside                  | accept               | accept                   |
 * | Valid QR, clearly outside         | needs approval       | accept + outside_fence   |
 * | Valid QR, location unavailable    | per unavailable mode | per unavailable mode     |
 * | QR for site A, GPS inside site B  | needs approval       | accept + site_mismatch   |
 * | Can't scan (no QR)                | needs approval       | accept + no_qr           |
 */
export function decidePunch(input: PolicyInput): PolicyDecision {
  if (input.qr === "invalid") return { outcome: "reject", reason: "invalid_qr" };

  const locFlags = locationFlag(input.location, input.insideOtherSite);

  if (input.qr === "none") {
    const flags: PunchFlag[] = ["no_qr", ...locFlags];
    return input.fenceMode === "block"
      ? { outcome: "needs_approval", reason: "no_qr", flags }
      : { outcome: "accept", flags };
  }

  // Valid QR from here on.
  if (input.location === "inside") return { outcome: "accept", flags: [] };

  if (input.location === "unavailable") {
    return input.locationUnavailableMode === "approval"
      ? { outcome: "needs_approval", reason: "location_unavailable", flags: locFlags }
      : { outcome: "accept", flags: locFlags };
  }

  // Clearly outside (possibly at another site).
  const reason: PunchFlag = input.insideOtherSite ? "site_mismatch" : "outside_fence";
  return input.fenceMode === "block"
    ? { outcome: "needs_approval", reason, flags: locFlags }
    : { outcome: "accept", flags: locFlags };
}

/** Effective settings for a site: per-site override, else the business default. */
export function effectiveModes(
  business: { fenceMode: FenceMode; locationUnavailableMode: LocationUnavailableMode },
  site: { fenceMode: FenceMode | null; locationUnavailableMode: LocationUnavailableMode | null },
): { fenceMode: FenceMode; locationUnavailableMode: LocationUnavailableMode } {
  return {
    fenceMode: site.fenceMode ?? business.fenceMode,
    locationUnavailableMode: site.locationUnavailableMode ?? business.locationUnavailableMode,
  };
}

export const FLAG_LABELS: Record<PunchFlag, string> = {
  outside_fence: "Outside geofence",
  location_unavailable: "Location unavailable",
  site_mismatch: "At a different site",
  no_qr: "No QR scan",
  offline_sync: "Sent later (offline)",
  not_assigned: "Not assigned to this site",
  late_clock_out: "Clocked out late",
};
